<!-- svelte-ignore a11y-label-has-associated-control -->
<!-- svelte-ignore a11y-missing-attribute -->

{#if data}
  <div class="columns">
    <div class="column">
      <div class="field">
        <label class="label">
          Title

          <div class="has-text-grey-light has-text-weight-normal">
            Describe what you need in a few words.
          </div>
        </label>

        <div class="control">
          <input
            class="input"
            type="text"
            placeholder=""
            bind:value={data.title} />
        </div>
      </div>

      <div class="field">
        <label class="label">
          What you need

          <div class="has-text-grey-light has-text-weight-normal">
            Describe in more detail what you need, including quantities and any
            timeframe.
          </div>
        </label>
        <div class="control">
          <textarea
            class="textarea"
            placeholder=""
            rows="10"
            bind:value={data.needs} />
        </div>
      </div>

      <div class="field">
        <label class="label">
          Why you need it

          <div class="has-text-grey-light has-text-weight-normal">
            Describe why you need the items or resources and how they will be
            used. If you can, include details of the project, or who or what
            will benefit.
          </div>
        </label>
        <div class="control">
          <textarea
            class="textarea"
            placeholder=""
            rows="10"
            bind:value={data.benefits} />
        </div>
      </div>

      <div class="field">
        <label class="label">
          About your charity (optional)

          <div class="has-text-grey-light has-text-weight-normal">
            Your charity profile will be visible to the business. Here you can
            include any additional information about your charity.
          </div>
        </label>
        <div class="control">
          <textarea
            class="textarea"
            placeholder=""
            rows="10"
            bind:value={data.background} />
        </div>
      </div>

      <div class="field">
        <label class="label">
          Image(s)

          <div class="has-text-grey-light has-text-weight-normal">
            Upload your charity logo, or any images that may help the business better understand your need.
          </div>
        </label>

        <div class="control">
          {#if data.images}
            {#each data.images as url, index}
              <div
                style="display:inline-block;position:relative;margin-right:8px;">
                <button
                  class="delete"
                  style="position:absolute;top:5px;right:5px;"
                  on:click={() => removeImage(index)} />
                <img src={url} style="height:200px;" />
                {#if index === 0}
                  <span
                    class="tag is-info"
                    style="position:absolute;bottom:10px;left:5px;">
                    Cover image
                  </span>
                {:else}
                  <button
                    class="tag is-info is-light"
                    style="position:absolute;bottom:10px;left:5px;cursor:pointer;border:none;"
                    on:click={() => setAsCoverImage(index)}>
                    Set as cover image
                  </button>
                {/if}
              </div>
            {/each}
          {/if}
          {#if fileUploads.length > 0}
            <div class="block">
              {#each fileUploads as fileUpload}
                {#if !fileUpload.complete}
                  <progress
                    class="progress"
                    value={fileUpload.progress}
                    max="100">{fileUpload.progress}</progress>
                {/if}
              {/each}
            </div>
          {/if}
          <div class="file">
            <label class="file-label">
              {#if isValid}
                <input
                  class="file-input"
                  type="file"
                  accept="image/*"
                  name="resume"
                  multiple
                  on:change={upload}
                  bind:this={filesElement} />
                <span class="file-cta">
                  <span class="file-label"> Choose file(s)... </span>
                </span>
              {/if}
            </label>
          </div>
        </div>
      </div>

      <div class="field">
        <label class="label">
          Location

          <div class="has-text-grey-light has-text-weight-normal">
            Where you need the items or resources. This will determine which local businesses are contacted about the need. 
          </div>
        </label>
        <div class="field is-grouped">
          <div class="control">
            {#if data.location}
              <input
                class="input"
                type="text"
                placeholder=""
                bind:value={data.location.postcode}
                readonly
                disabled />
            {/if}
          </div>
          <div class="control">
            <a class="button" on:click={editPostcode}>Change</a>
          </div>
        </div>
      </div>
    </div>
    <div class="column is-3">
      <div class="box">
        <p class="block"><b>Help &amp; advice</b></p>
        <p class="block">Tell us what you charity needs. It could be anything from office furniture, to meeting space.</p>
        <p class="block">This information will be published on the app where local businesses can see your need.</p>
        <p class="block">If there is a specific timeframe for your need, please include details.</p>
        <p class="block">If you need any extra help or have questions, please contact us at <a href="mailto:hello@agoodthing.org.uk">hello@agoodthing.org.uk</a>.</p>
      </div>
    </div>
  </div>
{/if}

<script lang="ts">
  export let data: Need_v1, isValid: boolean;
  import _ from 'lodash';
  import { appState } from '../../../lib/appState';
  import { uiState } from '../../../lib/uiState';
  import { v4 as uuidv4 } from 'uuid';
  import EditLocation from './EditLocation.svelte';

  if (!data.location || !data.location.postcode) {
    data.location = {
      postcode: $appState.charity!.contact.postcode,
      point: $appState.charity!.geo!
    };
  }

  isValid = true;

  function editPostcode() {
    $uiState.activeModal = {
      component: EditLocation,
      props: {
        setLocation: (postcode: string, point: GeoPoint) => {
          data.location = {
            postcode,
            point
          };
        }
      }
    };
  }

  let filesElement: any;

  let fileUploads: any[] = [];

  let firebase = window.firebase;

  async function updateProgress(
    index: number,
    progress: number,
    complete: boolean = false,
    url?: string
  ) {
    fileUploads[index].complete = complete;
    fileUploads[index].progress = progress;
    fileUploads[index].url = url;

    if (!isValid && _.every(fileUploads, 'complete')) {
      isValid = true;
      data.images = data.images || [];
      // preserve upload order
      fileUploads.forEach((upload) => {
        data.images!.push(upload.url);
      });
    }
  }

  function upload() {
    let files = filesElement.files;

    if (files) {
      fileUploads = [];

      isValid = false;

      const now = new Date();
      const imagesRef = firebase
        .storage()
        .ref()
        .child('images')
        .child(
          `${now.getUTCFullYear()}-${now.getUTCMonth()}-${now.getUTCDate()}`
        );

      for (let i = 0; i < files.length; i++) {
        let file = files[i];

        console.log(file);

        let name = `${uuidv4()}.jpg`;
        let ref = imagesRef.child(name);

        let fileUpload = {
          name,
          progress: 0,
          uploadTask: undefined as any
        };

        let uploadTask = ref.put(file);

        uploadTask.on(
          'state_changed',
          function (snapshot) {
            var progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log(progress);

            //fileUpload.progress = progress;
            updateProgress(i, progress);
          },
          function () {
            // failed
          },
          function () {
            console.log('complete');
            uploadTask.snapshot.ref
              .getDownloadURL()
              .then(function (downloadURL) {
                console.log('File available at', downloadURL);
                updateProgress(i, 100, true, downloadURL);
              });
          }
        );

        fileUpload.uploadTask = uploadTask;

        fileUploads = [...fileUploads, fileUpload];
      }
    }
  }

  function removeImage(index: number) {
    data.images = [
      ...data.images!.slice(0, index),
      ...data.images!.slice(index + 1)
    ];
  }

  function setAsCoverImage(index: number) {
    data.images = [data.images![index], ...data.images!];
    removeImage(index + 1);
  }
</script>
